import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';
import { getGeoLocation, submitLead, buildLeadPayload, handleLeadResponse } from '../services/apiClient';
import { validateEmail } from '../utils/validation';
import { X } from 'lucide-react';

const countryCodes = [
  { code: '+1', country: 'USA', flag: '🇺🇸' },
  { code: '+44', country: 'UK', flag: '🇬🇧' },
  { code: '+91', country: 'India', flag: '🇮🇳' },
  { code: '+61', country: 'Australia', flag: '🇦🇺' },
  { code: '+86', country: 'China', flag: '🇨🇳' },
  { code: '+81', country: 'Japan', flag: '🇯🇵' },
  { code: '+49', country: 'Germany', flag: '🇩🇪' },
  { code: '+33', country: 'France', flag: '🇫🇷' },
  { code: '+971', country: 'UAE', flag: '🇦🇪' },
  { code: '+65', country: 'Singapore', flag: '🇸🇬' },
  { code: '+92', country: 'Pakistan', flag: '🇵🇰' },
  { code: '+31', country: 'Netherlands', flag: '🇳🇱' },
];

const ContactModal = ({ isOpen, onClose, serviceName = 'Trademark Registration' }) => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [geoData, setGeoData] = useState(null);
  const [displayCountry, setDisplayCountry] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+1',
    phone: '',
    message: '',
  });

  // Fetch geolocation when modal opens
  useEffect(() => {
    if (isOpen) {
      const fetchGeoLocation = async () => {
        const data = await getGeoLocation();
        if (data.isSuccess && data.data) {
          setGeoData(data);
          setDisplayCountry(data.data.countryCode || '');
          // Set default country code
          const dialCode = getDialCodeFromCountry(data.data.countryCode);
          setFormData(prev => ({ ...prev, countryCode: dialCode }));
        }
      };
      fetchGeoLocation();
    }
  }, [isOpen]);

  const getDialCodeFromCountry = (countryCode) => {
    const dialCodes = {
      'US': '+1', 'CA': '+1', 'GB': '+44', 'AU': '+61', 'IN': '+91',
      'PK': '+92', 'NL': '+31', 'DE': '+49', 'FR': '+33', 'ES': '+34',
      'IT': '+39', 'BR': '+55', 'MX': '+52', 'JP': '+81', 'CN': '+86',
    };
    return dialCodes[countryCode] || '+1';
  };

  const handlePhoneChange = (value) => {
    // Only allow digits and limit to 15 characters
    const sanitized = value.replace(/\D/g, '').slice(0, 15);
    setFormData({ ...formData, phone: sanitized });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate email
    if (!validateEmail(formData.email)) {
      toast({
        title: 'Invalid Email',
        description: 'Please enter a valid email address.',
        variant: 'destructive',
      });
      return;
    }

    // Validate phone
    if (formData.phone.length < 7 || formData.phone.length > 15) {
      toast({
        title: 'Invalid Phone Number',
        description: 'Please enter a valid phone number (7-15 digits).',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Build payload
      const payload = buildLeadPayload(formData, geoData);

      // Submit lead
      const response = await submitLead(payload);

      // Handle response
      const success = handleLeadResponse(response, (errorMsg) => {
        toast({
          title: 'Error',
          description: errorMsg,
          variant: 'destructive',
        });
      });

      if (success) {
        toast({
          title: 'Success!',
          description: 'Redirecting to your application...',
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'An unexpected error occurred. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] p-0 overflow-y-auto sm:max-h-[95vh]">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 z-50 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none"
        >
          <X className="h-5 w-5 text-[#1E1E1E] md:text-white" />
          <span className="sr-only">Close</span>
        </button>
        
        <div className="grid md:grid-cols-2 gap-0">
          {/* Left Side - Image (Hidden on Mobile) */}
          <div className="hidden md:block relative h-full min-h-[500px] bg-gradient-to-br from-[#1E1E1E] via-[#2a2a2a] to-[#1E1E1E]">
            <div className="absolute inset-0">
              <img
                src="https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&q=80"
                alt="Legal Services"
                className="w-full h-full object-cover opacity-20"
              />
            </div>
            <div className="relative z-10 p-8 h-full flex flex-col justify-center text-white">
              <h3 className="text-3xl font-bold mb-3 text-white">Get Started Today</h3>
              <p className="text-base mb-6 leading-relaxed text-gray-200">
                Protect your brand with our professional trademark and legal services.
              </p>
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="bg-[#E5B80B] rounded-full p-2 mr-3 mt-1 flex-shrink-0">
                    <svg className="w-4 h-4 text-[#1E1E1E]" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-semibold">Expert Legal Team</h4>
                    <p className="text-sm text-gray-200">Experienced attorneys at your service</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="bg-[#E5B80B] rounded-full p-2 mr-3 mt-1 flex-shrink-0">
                    <svg className="w-4 h-4 text-[#1E1E1E]" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-semibold">Fast Processing</h4>
                    <p className="text-sm text-gray-200">Quick turnaround on all filings</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="bg-[#E5B80B] rounded-full p-2 mr-3 mt-1 flex-shrink-0">
                    <svg className="w-4 h-4 text-[#1E1E1E]" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-semibold">Affordable Pricing</h4>
                    <p className="text-sm text-gray-200">Starting from just $45</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Form */}
          <div className="p-4 sm:p-6 bg-white">
            <DialogHeader>
              <DialogTitle className="text-xl sm:text-2xl font-bold text-[#1E1E1E] mb-2">
                Request a Consultation
              </DialogTitle>
              <p className="text-xs sm:text-sm text-gray-600 mb-4">
                Fill out the form and we'll contact you within 24 hours.
              </p>
            </DialogHeader>

            {displayCountry && (
              <div className="mb-4 p-2 bg-[#E5B80B]/10 rounded-lg">
                <p className="text-xs sm:text-sm text-[#1E1E1E]">
                  <span className="font-semibold">Your Country:</span> {displayCountry}
                </p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4 mt-4">
              {/* Name Field */}
              <div>
                <label className="block text-xs sm:text-sm font-semibold text-[#1E1E1E] mb-1.5 sm:mb-2">
                  Full Name *
                </label>
                <Input
                  placeholder="Enter your full name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="h-10 sm:h-11 border-2 focus:border-[#E5B80B] text-sm sm:text-base"
                />
              </div>

              {/* Email Field */}
              <div>
                <label className="block text-xs sm:text-sm font-semibold text-[#1E1E1E] mb-1.5 sm:mb-2">
                  Email Address *
                </label>
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="h-10 sm:h-11 border-2 focus:border-[#E5B80B] text-sm sm:text-base"
                />
              </div>

              {/* Contact Number with Country Code */}
              <div>
                <label className="block text-xs sm:text-sm font-semibold text-[#1E1E1E] mb-1.5 sm:mb-2">
                  Contact Number *
                </label>
                <div className="flex gap-2 sm:gap-3">
                  <select
                    value={formData.countryCode}
                    onChange={(e) => setFormData({ ...formData, countryCode: e.target.value })}
                    className="h-10 sm:h-11 px-2 sm:px-3 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#E5B80B] focus:border-[#E5B80B] font-semibold text-[#1E1E1E] w-20 sm:w-28 bg-white text-xs sm:text-sm"
                  >
                    {countryCodes.map((country) => (
                      <option key={country.code} value={country.code}>
                        {country.flag} {country.code}
                      </option>
                    ))}
                  </select>
                  <Input
                    type="tel"
                    placeholder="Phone number"
                    value={formData.phone}
                    onChange={(e) => handlePhoneChange(e.target.value)}
                    required
                    maxLength={15}
                    className="h-10 sm:h-11 flex-1 border-2 focus:border-[#E5B80B] text-sm sm:text-base"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {formData.phone.length}/15 digits
                </p>
              </div>

              {/* Description Field */}
              <div>
                <label className="block text-xs sm:text-sm font-semibold text-[#1E1E1E] mb-1.5 sm:mb-2">
                  Description
                </label>
                <Textarea
                  placeholder="Tell us about your requirements..."
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="min-h-16 sm:min-h-20 border-2 focus:border-[#E5B80B] resize-none text-sm sm:text-base"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#E5B80B] hover:bg-[#c99f09] text-[#1E1E1E] font-semibold h-11 sm:h-12 text-base sm:text-lg transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Submitting...' : 'Register Your Trademark'}
              </Button>

              <p className="text-xs text-gray-500 text-center mt-3 sm:mt-4">
                By submitting this form, you agree to our Terms of Service and Privacy Policy.
              </p>
            </form>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ContactModal;
